import { createSignal, createEffect, Show, For, batch } from "solid-js";
import type { Accessor } from "solid-js";
import styles from "../../pages/Inventory.module.css";
import ItemStackView from "../ItemStackView";
import { fromItemDef, fromIdentifier } from "../../services/itemRefAdapters";
import Text from "../Text";
import QuantityInput from "../QuantityInput";
import { formatQuantity } from "../../services/quantityFormatter";
import { parseItemKey, type CraftingResolverService, type ItemDef } from "../../services/craftingResolver";
import type { ItemStack } from "../../models/itemStack";
import { useItemName } from '../../hooks/useItemName';
import { useTranslation } from "../../hooks/useTranslation";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import MoveItemsSection from "./MoveItemsSection";
import {
  useInventoryApi,
  type StorageSnapshot,
  type PricingMap,
  type ResolveResult,
  type RecipeDetail,
  type ChestInfo,
  type ChestSlotsResponse,
  type StorageInfo,
} from "../../services/inventoryApi";

export interface ItemDetailCardProps {
  selected: ItemDef;
  selectedStack?: ItemStack;
  activeSid: Accessor<string | null>;
  activeStorageName: Accessor<string>;
  activeStorageChests: Accessor<ChestInfo[]>;
  storageCache: Accessor<Record<string, StorageSnapshot>>;
  reservationCache: Accessor<Record<string, StorageSnapshot>>;
  allStorage: Accessor<StorageSnapshot>;
  pricing: Accessor<PricingMap>;
  profitMargin: () => number | null;
  resolver: CraftingResolverService;
  storages: Accessor<StorageInfo[]>;
  handleCraft: (key: string, times: number) => void;
  onPricingChange: (key: string, field: "buy" | "sell", raw: string) => void;
  onMoveSuccess?: (msg: string) => void;
  onMoveError?: (msg: string) => void;
}

const NOT_CRAFTABLE: ResolveResult = { status: "Missing", maxCraftable: 0, hasRecipe: false, allStatus: "Missing", allMaxCraftable: 0 };

export default function ItemDetailCard(props: ItemDetailCardProps) {
  const { t } = useTranslation();
  const { itemName } = useItemName();
  const inv = useInventoryApi();
  const sel = () => props.selected;

  const getSnap = (id: string | null): StorageSnapshot =>
    id ? (props.storageCache()[id] ?? {}) : {};
  const getRes = (id: string | null): StorageSnapshot =>
    id ? (props.reservationCache()[id] ?? {}) : {};

  const activeSnap = () => getSnap(props.activeSid());
  const activeRes = () => getRes(props.activeSid());
  const allSnap = () => props.allStorage();
  const qty = () => activeSnap()[sel().key] ?? 0;
  const reserved = () => activeRes()[sel().key] ?? 0;
  const availableQty = () => Math.max(0, qty() - reserved());
  const totalQty = () => allSnap()[sel().key] ?? 0;

  const [localResolve, setLocalResolve] = createSignal<ResolveResult>(NOT_CRAFTABLE);
  const [recipeDetail, setRecipeDetail] = createSignal<RecipeDetail | null>(null);

  const [showBreakdown, setShowBreakdown] = createSignal(false);
  const [chestSlotsCache, setChestSlotsCache] = createSignal<Record<string, ChestSlotsResponse | null>>({});
  const [loadingBreakdown, setLoadingBreakdown] = createSignal(false);
  let cancelBreakdown: (() => void) | null = null;

  createEffect(() => {
    props.activeSid();
    sel();
    props.activeStorageChests();
    batch(() => {
      cancelBreakdown?.();
      cancelBreakdown = null;
      setShowBreakdown(false);
      setChestSlotsCache({});
      setLoadingBreakdown(false);
    });
  });

  async function fetchBreakdown() {
    const sid = props.activeSid();
    const chests = props.activeStorageChests();
    if (!sid || chests.length === 0) return;
    let cancelled = false;
    cancelBreakdown = () => { cancelled = true; };
    setLoadingBreakdown(true);
    try {
      const results = await Promise.allSettled(chests.map(c => inv.getChestSlots(sid, c.id)));
      if (cancelled) return;
      const cache: Record<string, ChestSlotsResponse | null> = {};
      results.forEach((r, i) => { cache[chests[i].id] = r.status === "fulfilled" ? r.value : null; });
      setChestSlotsCache(cache);
    } finally {
      if (!cancelled) setLoadingBreakdown(false);
      cancelBreakdown = null;
    }
  }

  function toggleBreakdown() {
    if (!showBreakdown()) {
      setShowBreakdown(true);
      if (Object.keys(chestSlotsCache()).length === 0) fetchBreakdown().catch(() => {});
    } else {
      setShowBreakdown(false);
    }
  }

  const breakdownRows = () => {
    const cache = chestSlotsCache();
    const item = sel();
    // Third key segment is the NBT hash; without it a custom item would count every other variant
    // of the same block sitting in the chest.
    const nbtHash = item.key.split(":")[2];
    return props.activeStorageChests()
      .map(c => {
        const data = cache[c.id];
        if (data == null) return null;
        const count = data.slots
          .filter(s => s.itemId === item.id && s.metadata === item.metadata && (s.nbtHash ?? undefined) === nbtHash)
          .reduce((sum, s) => sum + s.count, 0);
        return { chest: c, count, scanned: !!data.lastScannedAt };
      })
      .filter((r): r is { chest: ChestInfo; count: number; scanned: boolean } => r !== null && r.count > 0);
  };

  const scannedChestCount = () => {
    const cache = chestSlotsCache();
    return props.activeStorageChests().filter(c => cache[c.id]?.lastScannedAt).length;
  };

  createEffect(() => {
    const sid = props.activeSid();
    props.storageCache(); // re-fetch when storage contents change
    if (!sid) return;
    const hash = parseItemKey(sel().key)?.hash;
    inv.resolveItem(sid, sel().key).then(setLocalResolve).catch(() => {});
    inv.fetchRecipeDetail(sel().id, sel().metadata, sid, hash).then(setRecipeDetail).catch(() => {});
  });

  const craftingEnabled = useFeatureFlag("CRAFTING");
  const canCraftLocal = () => craftingEnabled() && localResolve().hasRecipe && localResolve().maxCraftable > 0;
  const craftCountLocal = () => localResolve().maxCraftable;

  function getPricing(key: string): { buy?: number; sell?: number } {
    return props.pricing()[key] ?? {};
  }

  function craftCostPer64(): number | null {
    const recipe = recipeDetail();
    if (!recipe) return null;
    let costPerCraft = 0;
    for (const ing of recipe.ingredients) {
      const ingPrice = getPricing(ing.key).buy;
      if (ingPrice == null) return null;
      costPerCraft += (ing.count / 64) * ingPrice;
    }
    return (costPerCraft / recipe.resultCount) * 64;
  }

  return (
    <div class={styles.detailCard}>
      <div class={styles.detailHeader}>
        <Show
          when={props.selectedStack}
          fallback={
            <>
              <ItemStackView stack={fromItemDef(sel())} size={48} />
              <div>
                <div class={styles.detailTitle}>{itemName(sel().key)}</div>
                <div class={styles.detailId}>ID: {sel().id}:{sel().metadata}</div>
              </div>
            </>
          }
        >
          {/* A zero count is an empty slot to ItemStackView; the rows below carry the amount. */}
          {(stack) => <ItemStackView stack={{ ...stack(), count: Math.max(qty(), 1) }} size={48} inlineTooltip showCount={false} />}
        </Show>
      </div>

      <div class={styles.detailSection}>
        <div class={styles.detailRow}>
          <span class={styles.detailLabel}><Text val="INVENTORY_STOCK_THIS" /></span>
          <span class={styles.detailValue}>{formatQuantity(qty(), sel().stackSize)}</span>
        </div>
        <Show when={reserved() > 0}>
          <div class={styles.detailRow}>
            <span class={styles.detailLabel}><Text val="INVENTORY_RESERVED" /></span>
            <span class={`${styles.detailValue} ${styles.reservedValue}`}>{formatQuantity(reserved(), sel().stackSize)}</span>
          </div>
          <div class={styles.detailRow}>
            <span class={styles.detailLabel}><Text val="INVENTORY_AVAILABLE" /></span>
            <span class={styles.detailValue}>{formatQuantity(availableQty(), sel().stackSize)}</span>
          </div>
        </Show>
        <div class={styles.detailRow}>
          <span class={styles.detailLabel}><Text val="INVENTORY_STOCK_ALL" /></span>
          <span class={styles.detailValue}>{formatQuantity(totalQty(), sel().stackSize)}</span>
        </div>
        <div class={styles.detailRow}>
          <span class={styles.detailLabel}><Text val="INVENTORY_CRAFTABLE" /></span>
          <span class={`${styles.detailValue} ${canCraftLocal() ? styles.available : styles.unavailable}`}>
            {canCraftLocal() ? t("INVENTORY_CRAFTABLE_YES", formatQuantity(craftCountLocal(), sel().stackSize)) : localResolve().hasRecipe ? <Text val="INVENTORY_MISSING_INGREDIENTS" /> : <Text val="INVENTORY_NO_RECIPE" />}
          </span>
        </div>
      </div>

      <Show when={recipeDetail()}>
        {(r) => (
          <div class={styles.detailSection}>
            <div class={styles.detailSectionTitle}>{t("INVENTORY_RECIPE_YIELDS", r().resultCount)}</div>
            <For each={r().ingredients}>
              {(ing) => {
                const inStock = () => ing.stock >= ing.count;
                const craftable = () => !inStock() && ing.hasRecipe && ing.maxCraftable > 0;
                return (
                  <div class={styles.detailIngredient}>
                    <ItemStackView stack={fromIdentifier(`minecraft:${ing.itemName}`, ing.metadata >= 0 ? ing.metadata : 0)} size={24} />
                    <span class={inStock() || craftable() ? styles.available : styles.unavailable}>
                      {ing.count}x {itemName(ing.key)}
                    </span>
                    <span class={styles.detailIngCount}>
                      {ing.stock}/{ing.count}
                      {ing.maxCraftable > 0 ? ` (+${ing.maxCraftable})` : ""}
                    </span>
                  </div>
                );
              }}
            </For>
            <Show when={canCraftLocal()}>
              <div class={styles.craftPresets}>
                <button class={styles.detailCraftBtn} onClick={() => props.handleCraft(sel().key, r().resultCount)} disabled={craftCountLocal() < r().resultCount}>1x</button>
                <button class={styles.detailCraftBtn} onClick={() => props.handleCraft(sel().key, 32)} disabled={craftCountLocal() < 32}>32x</button>
                <button class={styles.detailCraftBtn} onClick={() => props.handleCraft(sel().key, 64)} disabled={craftCountLocal() < 64}>64x</button>
                <button class={`${styles.detailCraftBtn} ${styles.craftMax}`} onClick={() => props.handleCraft(sel().key, craftCountLocal())} disabled={craftCountLocal() <= 0}>Max ({craftCountLocal()})</button>
                <div class={styles.craftAmountWrap}>
                  <QuantityInput value={0} onChange={() => {}} onSubmit={(val) => props.handleCraft(sel().key, val)} stackSize={sel().stackSize} presets={false} placeholder={t("INVENTORY_QUANTITY_PLACEHOLDER")} />
                </div>
              </div>
            </Show>
          </div>
        )}
      </Show>

      <Show when={props.activeStorageChests().length > 0}>
        <div class={styles.detailSection}>
          <div class={styles.detailSectionTitle} style="cursor:pointer;display:flex;align-items:center;justify-content:space-between" onClick={toggleBreakdown}>
            <span><Text val="INVENTORY_CHEST_BREAKDOWN" /></span>
            <i class={`fa-solid fa-chevron-${showBreakdown() ? "up" : "down"}`} style="font-size:10px" />
          </div>
          <Show when={showBreakdown()}>
            <Show when={loadingBreakdown()}>
              <div class={styles.detailLabel} style="padding:4px 0"><i class="fa-solid fa-spinner fa-spin" style="margin-right:6px" /><Text val="LOADING" /></div>
            </Show>
            <Show when={!loadingBreakdown()}>
              <Show when={scannedChestCount() === 0}>
                <div class={styles.breakdownEmpty}><Text val="INVENTORY_BREAKDOWN_NO_SCAN" /></div>
              </Show>
              <Show when={scannedChestCount() > 0}>
                <Show when={breakdownRows().length === 0}>
                  <div class={styles.breakdownEmpty}><Text val="INVENTORY_BREAKDOWN_NOT_FOUND" /></div>
                </Show>
                <For each={breakdownRows()}>
                  {(row) => (
                    <div class={styles.breakdownRow}>
                      <span class={styles.breakdownCoords}>
                        ({row.chest.x}, {row.chest.y}, {row.chest.z})
                        <Show when={row.chest.x2 != null}>
                          <span style="opacity:0.5"> +</span> ({row.chest.x2}, {row.chest.y2}, {row.chest.z2})
                        </Show>
                      </span>
                      <span class={styles.detailValue}>{row.count}</span>
                    </div>
                  )}
                </For>
              </Show>
              <Show when={props.activeStorageChests().some(c => !c.lastScannedAt)}>
                <div class={styles.breakdownNotScanned}>
                  {t("INVENTORY_BREAKDOWN_PENDING", props.activeStorageChests().filter(c => !c.lastScannedAt).length)}
                </div>
              </Show>
            </Show>
          </Show>
        </div>
      </Show>

      <Show when={props.activeSid()}>
        <MoveItemsSection
          item={sel()}
          sourceStorageId={props.activeSid}
          availableQty={availableQty}
          storages={props.storages}
          onSuccess={props.onMoveSuccess ?? (() => {})}
          onError={props.onMoveError ?? (() => {})}
        />
      </Show>

      <div class={styles.detailSection}>
        <div class={styles.detailSectionTitle}><Text val="INVENTORY_PRICING" /></div>
        <Show when={localResolve().hasRecipe}>
          <div class={styles.detailRow}>
            <span class={styles.detailLabel}><Text val="INVENTORY_COST_PRICE" /></span>
            <span class={styles.detailValue}>
              {(() => { const cost = craftCostPer64(); return cost != null ? cost.toFixed(2) : "-"; })()}
            </span>
          </div>
        </Show>
        <div class={styles.detailRow}>
          <span class={styles.detailLabel}><Text val="INVENTORY_BUY_PRICE" /></span>
          <input class={styles.priceInput} type="number" placeholder="-"
            value={getPricing(sel().key).buy ?? ""}
            onInput={(e) => props.onPricingChange(sel().key, "buy", e.currentTarget.value)} />
        </div>
        <div class={styles.detailRow}>
          <span class={styles.detailLabel}><Text val="INVENTORY_SELL_PRICE" /></span>
          {(() => {
            const baseCost = () => getPricing(sel().key).buy ?? craftCostPer64();
            const suggested = () => {
              const base = baseCost();
              if (base == null || props.profitMargin() == null) return null;
              return base * (1 + props.profitMargin()!);
            };
            const showBtn = () => !getPricing(sel().key).sell && suggested() != null;
            return (
              <div class={styles.priceInputGroup}>
                <input class={`${styles.priceInput} ${showBtn() ? styles.priceInputWithBtn : ""}`} type="number" placeholder="-"
                  value={getPricing(sel().key).sell ?? ""}
                  onInput={(e) => props.onPricingChange(sel().key, "sell", e.currentTarget.value)} />
                <Show when={showBtn()}>
                  <button
                    class={styles.applyMarginBtn}
                    title={t("INVENTORY_APPLY_SELL_MARGIN", Math.round((props.profitMargin()! ?? 0) * 100))}
                    onClick={() => {
                      const val = suggested();
                      if (val != null) props.onPricingChange(sel().key, "sell", val.toFixed(2));
                    }}
                  >
                    <i class="fa-solid fa-arrow-right" style="margin-right: 4px; font-size: 10px; opacity: 0.6" />
                    {suggested()?.toFixed(2)}
                  </button>
                </Show>
              </div>
            );
          })()}
        </div>
        <div class={styles.detailRow}>
          <span class={styles.detailLabel}><Text val="INVENTORY_JOB_PRICE" /></span>
          <span class={styles.detailValue}>-</span>
        </div>
      </div>
    </div>
  );
}
